import Grid from '@mui/material/Grid';
import type { OptionsMap } from '@openx/types/options.ts';
import type { GeographicTargetingState } from '@openx/types/targeting/geographic.ts';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import { OptionsDropdown } from '../../shared/OptionsDropdown/OptionsDropdown.tsx';

type LocationSourceProps = {
  children: ReactNode;
  locationSourceState: GeographicTargetingState['location_source'];
  onSelect: (newData: GeographicTargetingState['location_source']) => void;
};

export const LocationSource = ({ children, locationSourceState, onSelect }: LocationSourceProps) => {
  const { useLocationSourceOptionsFetch } = useTargetingContext();

  if (useLocationSourceOptionsFetch) {
    return (
      <RenderLocationSource
        locationSourceState={locationSourceState}
        onSelect={onSelect}
        useLocationSourceOptionsFetch={useLocationSourceOptionsFetch}
      >
        {children}
      </RenderLocationSource>
    );
  }

  return null;
};

const RenderLocationSource = ({
  children,
  locationSourceState,
  onSelect,
  useLocationSourceOptionsFetch,
}: LocationSourceProps & {
  useLocationSourceOptionsFetch: () => { isLoading: boolean; locationSourceOptions: OptionsMap };
}) => {
  const { t } = useTranslation();
  const { isLoading, locationSourceOptions } = useLocationSourceOptionsFetch();

  return (
    <Grid
      container
      spacing={1}
      sx={{
        alignItems: 'flex-end',
      }}
    >
      <Grid size={3}>{children}</Grid>
      <Grid size={9}>
        <OptionsDropdown
          loading={isLoading}
          options={locationSourceOptions}
          onChange={value => onSelect({ ...locationSourceState, val: value })}
          selectedOptions={locationSourceState.val as Set<string>}
          placeholder={t('[ Select a Location Source ]')}
          dataTest="location-source-input"
        />
      </Grid>
    </Grid>
  );
};
